/**
 * RunTimelineInspector — paired investigation timelines for a run.
 *
 * Network / Thoughts / Console, each synced to the video's current time and
 * seekable. Sits under the video on the canonical run detail page with a slim
 * vertical icon-tab rail on its left, so the agent's reasoning, console output,
 * and network activity can each be replayed against the recorded browser
 * actions.
 *
 * Thoughts are always available; Console/Network are lazy-loaded the first time
 * their tab is opened (via `onLoadDiagnostics`), matching the video player.
 *
 * @module ui/pages/RunDetail/RunTimelineInspector
 */

import React from 'react';
import { Brain, Terminal, Globe } from '@phosphor-icons/react';
import {
  ConsoleDiagnosticsPanel,
  NetworkDiagnosticsPanel,
  ThoughtsDiagnosticsPanel,
  type TimestampedThought,
} from '@chatsdet/ui';
import type { TimestampedConsoleLog, TimestampedNetworkRequest } from '@chatsdet/types';
import { VerticalIconTabs } from './VerticalIconTabs';

type InspectorTab = 'network' | 'thoughts' | 'console';

export interface RunTimelineInspectorProps {
  /**
   * Agent reasoning. Omit entirely for runs that have none — external suites are
   * the customer's own code, so there is no agent and therefore no thoughts.
   * The tab is then hidden rather than shown permanently empty.
   */
  thoughts?: TimestampedThought[];
  consoleLogs?: TimestampedConsoleLog[];
  networkRequests?: TimestampedNetworkRequest[];
  /** Current video time in milliseconds */
  currentTimeMs: number;
  /** Seek the video to a timestamp (ms) */
  onSeek: (timestampMs: number) => void;
  /** ISO timestamp of when the video started, for absolute-time tooltips */
  videoStartedAt?: string | null;
  /** Whether console/network diagnostics are loading */
  diagnosticsLoading?: boolean;
  /** Called the first time a diagnostics (console/network) tab is opened */
  onLoadDiagnostics?: () => void;
  /**
   * Maps a full-video timestamp (ms) to the timebase shown in the relative
   * "M:SS into the video" labels. Supplied as a full→fast map when the fast
   * variant plays so labels match the scrubber; omit (identity) in full mode.
   * Only affects the relative label — sync/seek and absolute time stay full.
   */
  displayTimeMs?: (fullMs: number) => number;
  className?: string;
}

export const RunTimelineInspector: React.FC<RunTimelineInspectorProps> = ({
  thoughts,
  consoleLogs,
  networkRequests,
  currentTimeMs,
  onSeek,
  videoStartedAt,
  diagnosticsLoading,
  onLoadDiagnostics,
  displayTimeMs,
  className,
}) => {
  const [activeTab, setActiveTab] = React.useState<InspectorTab>('network');

  // Lazy-load console/network the first time those tabs are opened (network is
  // the default, so diagnostics start loading on mount).
  React.useEffect(() => {
    if ((activeTab === 'console' || activeTab === 'network') && onLoadDiagnostics) {
      onLoadDiagnostics();
    }
  }, [activeTab, onLoadDiagnostics]);

  return (
    <div className={`flex min-h-0 overflow-hidden bg-card ${className ?? ''}`}>
      <VerticalIconTabs
        selectedId={activeTab}
        onSelect={(id) => setActiveTab(id as InspectorTab)}
        items={[
          { id: 'network', icon: <Globe size={18} />, label: 'Network', count: networkRequests?.length },
          ...(thoughts
            ? [
                {
                  id: 'thoughts',
                  icon: <Brain size={18} weight={activeTab === 'thoughts' ? 'fill' : 'regular'} />,
                  label: 'Thoughts',
                  count: thoughts.length,
                },
              ]
            : []),
          { id: 'console', icon: <Terminal size={18} />, label: 'Console', count: consoleLogs?.length },
        ]}
      />

      {/* Panels — keep mounted so scroll/expand state survives tab switches. */}
      <div className="min-w-0 flex-1">
        {thoughts && (
          <div className={`h-full ${activeTab === 'thoughts' ? '' : 'hidden'}`}>
            <ThoughtsDiagnosticsPanel
              thoughts={thoughts}
              currentTimeMs={currentTimeMs}
              onSeek={onSeek}
              videoStartedAt={videoStartedAt}
              displayTimeMs={displayTimeMs}
            />
          </div>
        )}
        <div className={`h-full ${activeTab === 'console' ? '' : 'hidden'}`}>
          <ConsoleDiagnosticsPanel
            logs={consoleLogs ?? []}
            currentTimeMs={currentTimeMs}
            onSeek={onSeek}
            isLoading={diagnosticsLoading}
            videoStartedAt={videoStartedAt}
            displayTimeMs={displayTimeMs}
          />
        </div>
        <div className={`h-full ${activeTab === 'network' ? '' : 'hidden'}`}>
          <NetworkDiagnosticsPanel
            requests={networkRequests ?? []}
            currentTimeMs={currentTimeMs}
            onSeek={onSeek}
            isLoading={diagnosticsLoading}
            videoStartedAt={videoStartedAt}
            displayTimeMs={displayTimeMs}
          />
        </div>
      </div>
    </div>
  );
};
